'use client'

import React from 'react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import UtxoManager from '@/components/search-input/pages/utxo-manager'

const usd = (n: number) =>
	n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

/**
 * The split prompt is the split tool: the wallet's UTXO manager, unchanged,
 * under a short statement of why. The manager owns selection, sizing, the
 * invoice, signing and the success toast; it calls `onDone` after a
 * broadcast. Dismissing (X, Escape, "Not now") calls `onLater`.
 */
export default function SplitPromptDialog({
	open,
	largestSats,
	btcUsd,
	onInteract,
	onDone,
	onLater,
	manager
}: {
	open: boolean
	largestSats: number
	btcUsd?: number | null
	/** First pointer or key input inside the manager. */
	onInteract?: () => void
	onDone: () => void
	onLater: () => void
	/** Test/preview seam; defaults to the wallet's UTXO manager. */
	manager?: React.ReactNode
}) {
	const btc = (largestSats / 1e8).toLocaleString('en-US', { maximumFractionDigits: 8 })
	const dollars = btcUsd ? usd((largestSats / 1e8) * btcUsd) : null
	const interacted = React.useRef(false)
	const interact = () => {
		if (interacted.current) return
		interacted.current = true
		onInteract?.()
	}
	return (
		<Dialog open={open} onOpenChange={(next) => (next ? undefined : onLater())}>
			<DialogContent
				className="max-h-[92dvh] max-w-lg gap-0 overflow-y-auto p-0 focus:outline-none focus-visible:outline-none"
				data-testid="split-prompt"
				onOpenAutoFocus={(e) => e.preventDefault()}
			>
				<header className="px-5 pb-4 pt-5 sm:px-6">
					<DialogTitle className="text-base font-semibold text-foreground">
						Split your largest UTXO
					</DialogTitle>
					<p className="mt-3 flex flex-wrap items-baseline gap-x-2 tabular-nums">
						<span className="text-3xl font-semibold tracking-tight text-foreground">
							{dollars ?? `${btc} BTC`}
						</span>
						{dollars && <span className="text-sm text-muted-foreground">{btc} BTC</span>}
						<span className="text-sm text-muted-foreground">in one UTXO</span>
					</p>
					<DialogDescription className="mt-2 max-w-[60ch] text-sm leading-relaxed text-muted-foreground">
						Each purchase spends a whole UTXO, and its change waits about 10 minutes for a
						confirmation. Smaller pieces let you buy several items back to back. Your bitcoin stays
						in your wallet.
					</DialogDescription>
				</header>
				<div
					className="border-t border-border"
					onPointerDownCapture={interact}
					onKeyDownCapture={interact}
				>
					{manager ?? <UtxoManager onClose={onDone} onNavigate={() => {}} args={{ embedded: true }} />}
				</div>
				<footer className="flex items-center justify-between border-t border-border px-5 py-3 sm:px-6">
					<span className="text-xs text-muted-foreground">We&apos;ll ask again in a week.</span>
					<button
						type="button"
						onClick={onLater}
						className="rounded-md px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						Not now
					</button>
				</footer>
			</DialogContent>
		</Dialog>
	)
}
